import { MediaControlReq, EMedia } from '@/pb/protocol_media_control_pb'
import { TSigSessionControl, ESig, SigSessionCtrlReqParam } from '@/pb/signaling_param_pb'
import mqttcomm from '@/mqttcomm'
import { CMD } from '@/pb/protocol_cmd_pb'
import { IMMsgRpcReq, IMMsgSendReq } from '@/pb/protocol_msg_pb'
import { EMsg, TMsgBrief, TMsgEntity } from '@/pb/msg_entity_pb'
import { CSync } from '@/pb/sync_pb'
import { MessageIds } from '@/pb/message_pb'
import { Gate } from '@/pb/gate_pb'
import { getUniNumber } from '@/utils/uniNumber'
import { Entity } from '@/pb/entity_pb'

/**
 * 控制自己或别人的媒体设备，麦克风，摄像头，扬声器
 * @param context store context
 * @param controlOption ESig.ControlOptions
 * @param on true: 打开， false: 关闭
 * @param userId 目标用户id
 * @param fvalue
 */
function controlMediaDevice(context, controlOption, on, userId, uvalue, fvalue, type) {
  if (context.rootState.status !== mqttcomm.MachineStatus.SessionEstablished) {
    console.warn('当前不在会话中')
    return
  }
  const req = new MediaControlReq([
    /* base: */ [
      /* header: */ mqttcomm.generateBasePacketHeader(context)
    ],
    /* sourceType: */ EMedia.SourceType.FROM_SIGNALING,
    /* sourceId: */ context.rootState.currentSession.roomId,
    /* ownerId: */ userId,
  ])

  // data = [op,onoff,uvalue,svalue,fvalue] TSigSessionControl传参的顺序
  // type force焦点控制时,controlOption的值为camera
  const data = type === 'force' ? [controlOption, true, uvalue, '', fvalue] : [controlOption, on]
  // 区分焦点控制和其他媒体控制
  req.addControls(new TSigSessionControl(data))
  req.cmd = CMD.MEDIA_CONTROL_REQ
  mqttcomm.doPublish(context, req)
}

// 控制本地用户媒体
function controlLocalMedia (context, controlOption, on) {
  controlMediaDevice(context, controlOption, on, context.rootState.userInfo.id)
}

// 发送冻屏、激光笔、屏幕共享等独占功能的信令
function sendSyncMediaMqtt (context, payload) {
  const { op, on, sValue } = payload
  const req = new IMMsgRpcReq([
    /* base: */ [mqttcomm.generateBasePacketHeader(context)],
    /* requestid: */ mqttcomm.generateRequestId(),
    /* reservedid: */ context.rootState.currentSession.roomId,
    /* rpcType: */ EMsg.RPCType.RPC_TYPE_SIGNALING_SESSION_CTRL_REQ,
    /* encodeType: */ EMsg.EncodeType.PROBUF_FORMAT_ENCODING,
    /* acceptEncType: */ EMsg.EncodeType.JSON_FORMAT_ENCODING,
    /* param: */ new SigSessionCtrlReqParam([
      /* roomid: */ context.rootState.currentSession.roomId,
      /* roomkey: */ context.rootState.currentSession.roomKey,
      /* controlReqId: */ mqttcomm.generateRequestId(),
      /* controlUserId: */ context.rootState.userInfo.id,
      /* controlledUserId: */ context.rootState.userInfo.id,
      [
        op,
        on,
        '',
        sValue
      ]
    ]).serializeBinary()
  ])
  req.cmd = CMD.IM_MSG_RPC_REQ
  mqttcomm.doPublish(context, req)
}

export default {
  actions: {
    controlCamera: (context, on/* DeviceStatus.on | DeviceStatus.off */) => {
      controlLocalMedia(context, ESig.ControlOption.CAMERA, on)
    },
    controlSpeaker: (context, on/* DeviceStatus.on | DeviceStatus.off */) => {
      controlLocalMedia(context, ESig.ControlOption.SPEAKER, on)
    },
    controlMicrophone: (context, on/* DeviceStatus.on | DeviceStatus.off */) => {
      controlLocalMedia(context, ESig.ControlOption.MICROPHONE, on)
    },
    // 向服务器发送屏幕共享相关的申请
    fakeControlScreenSharing: (context, on/* DeviceStatus.on | DeviceStatus.off */) => {
      // 注意此处的op，其实是摄像头的op=2，而非是屏幕共享
      // 其他端用摄像头来传输屏幕视频流，用105来控制一些互斥的逻辑
      controlLocalMedia(context, ESig.ControlOption.CAMERA, on)
    },
    // 发送真正的屏幕共享信令
    realControlScreenSharing: (context, on) => {
      const params = {
        op: ESig.ControlOption.ONESELF_SCREEN_SHARING,
        on,
        sValue: ''
      }
      sendSyncMediaMqtt(context, params)
    },
    // 主动开启或关闭白板的请求
    toggleLocalUserWhiteBoard (context, payload) {
      const { on, color } = payload
      const params = {
        op: ESig.ControlOption.ONESELF_WHITEBOARD, // ONESELF_WHITEBOARD #213B3E
        on,
        sValue: on ? color : ''
      }
      sendSyncMediaMqtt(context, params)
    },
    // 主动开启或关闭冻屏的请求
    toggleLocalUserFreeze (context, payload) {
      const { on, imageUrl } = payload
      const params = {
        op: ESig.ControlOption.ONESELF_FREEZE,
        on,
        sValue: on ? imageUrl : ''
      }
      sendSyncMediaMqtt(context, params)
    },
    // 主动开启或关闭激光笔的请求
    toggleLocalUserRealtimeMark (context, payload) {
      const { on, userId } = payload
      const params = {
        op: ESig.ControlOption.ONESELF_REALTIME_ANNOTATION,
        on,
        sValue: on ? userId : ''
      }
      sendSyncMediaMqtt(context, params)
    },
    // 发送冻屏、激光笔相关的标注mqtt信令，增加万事贴
    sendLocalSyncMqtt (context, sData) {
      const notifyData = new CSync.Notify([
        context.rootState.userInfo.id
      ])
      notifyData.addMsgs(sData)

      const msgData = new Gate.MsgData([
        MessageIds.MSG_CSYNC_NOTIFY,
        notifyData.serializeBinary()
      ])

      const brief = new TMsgBrief([
        EMsg.MsgMode.SIGNALING_SESSION_MSG,
        EMsg.MsgType.MSG_TYPE_POINT_ANNOTATION_OLD,
        EMsg.EncodeType.PROBUF_FORMAT_ENCODING,
        mqttcomm.generateRequestId()
      ])
      brief.setSenderid(context.rootState.userInfo.id)
      brief.setReceiverid(context.rootState.currentSession.roomId)

      const entity = new TMsgEntity([
        '',
        msgData.serializeBinary()
      ])
      entity.setBrief(brief)

      const req = new IMMsgSendReq([
        /* base: */ [mqttcomm.generateBasePacketHeader(context)]
      ])
      req.setMsg(entity)
      req.cmd = CMD.IM_MSG_SEND_REQ
      mqttcomm.doPublish(context, req)
    },
    // 本地用户白板贴图 和 本地用户上传附件
    localUserSticker (context, { content, conferenceId }) {
      const brief = new TMsgBrief([
        EMsg.MsgMode.SIGNALING_SESSION_MSG,
        EMsg.MsgType.MSG_TYPE_POINT_ANNOTATION_OLD,
        EMsg.EncodeType.PROBUF_FORMAT_ENCODING,
        mqttcomm.generateRequestId()
      ])
      brief.setSenderid(context.rootState.userInfo.id)
      brief.setReceiverid(context.rootState.currentSession.roomId)

      const msg = new Entity.ChannelMsgSendReq([
        context.rootState.currentSession?.roomId, // 频道ID
        context.getters.localUser.id, // 用户ID
        conferenceId, // 所属群组ID
        getUniNumber(), // 客户端生成的消息序列号
        JSON.stringify(content)
      ])

      const gateMsgData = new Gate.MsgData([
        MessageIds.MSG_CHANNEL_CHANNELMSGSENDREQ,
        msg.serializeBinary()
      ])

      const entity = new TMsgEntity([
        '',
        gateMsgData.serializeBinary()
      ])
      entity.setBrief(brief)

      const req = new IMMsgSendReq([
        /* base: */ [mqttcomm.generateBasePacketHeader(context)]
      ])
      req.setMsg(entity)
      req.cmd = CMD.IM_MSG_SEND_REQ
      mqttcomm.doPublish(context, req)
    },
    // 本地用户画线
    localUserDrawLine (context, line) {
      const drawData = new CSync.DrawDataWebNotify([
        line.posxList,
        line.posyList,
        line.color,
        line.number,
        line.width,
        context.rootState.userInfo.id
      ])

      const sData = new CSync.SData([
        MessageIds.MSG_CSYNC_DRAWDATANOTIFY,
        drawData.serializeBinary()
      ])

      context.dispatch('sendLocalSyncMqtt', sData)
    },
    // 本地用户撤销画线
    localUserUndoDraw (context) {
      const drawData = new CSync.DrawRevokeNotify([
        1,
        context.rootState.userInfo.id
      ])

      const sData = new CSync.SData([
        MessageIds.MSG_CSYNC_DRAWREVOKENOTIFY,
        drawData.serializeBinary()
      ])

      context.dispatch('sendLocalSyncMqtt', sData)
    },
    // 本地用户清空画线
    localUserClearDraw (context) {
      const drawData = new CSync.DrawClearNotify([
        context.rootState.userInfo.id
      ])

      const sData = new CSync.SData([
        MessageIds.MSG_CSYNC_DRAWCLEARNOTIFY,
        drawData.serializeBinary()
      ])

      context.dispatch('sendLocalSyncMqtt', sData)
    },
    // 本地用户激光笔打点标注
    localUserRealtimeMark (context, point) {
      const pointData = new CSync.SyncMarkNotify([
        point.color,
        point.posX,
        point.posY,
        context.rootState.userInfo.id, // 控制人id，即本地用户
        context.rootGetters.realtimeMarkOnUser?.realtimeMarkTargetUserId || context.rootState.userInfo.id // 被标注的视频用户id
      ])

      const sData = new CSync.SData([
        MessageIds.MSG_CSYNC_SYNCMARKNOTIFY,
        pointData.serializeBinary()
      ])

      context.dispatch('sendLocalSyncMqtt', sData)
    },
    // 主动开启或关闭万事贴的请求
    toggleSlamMark (context, payload) {
      const { on, userId } = payload
      const params = {
        op: ESig.ControlOption.ONESELF_SLAM_ANNOTATION,
        on,
        sValue: on ? userId : ''
      }
      sendSyncMediaMqtt(context, params)
    },
    // 本地用户万事贴标注
    // im_common\src\hiar.im\common\proto_def\define\client\sync.proto
    localUserSlamMark (context, point) {
      const slamMarkData = new CSync.SlamMarkNotify([
        point.color,
        point.posX,
        point.posY,
        context.rootState.userInfo.id, // 控制人id，即本地用户
        context.rootGetters.slamMarkOnUser?.slamMarkTargetUserId || context.rootState.userInfo.id // 被标注的视频用户id
      ])

      const sData = new CSync.SData([
        MessageIds.MSG_CSYNC_SYNCMARKNOTIFY,
        slamMarkData.serializeBinary()
      ])

      context.dispatch('sendLocalSyncMqtt', sData)
    },
    // 控制其他人的媒体，例如以主持人身份关闭其他用户的音视频
    controlOtherMemberMedia (context, { option, on, userId, uvalue, fvalue, type }) {
      controlMediaDevice(context, option, on, userId, uvalue, fvalue, type)
    }
  }
}
